import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import { site, formOptions } from "@/config/site";
import { submitContact, parseError, newToken } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-ember focus:outline-none focus:ring-2 focus:ring-ember/25";

export function ContactForm() {
  const navigate = useNavigate();
  const [values, setValues] = useState({ fullName: "", email: "", phone: "", contactMethod: "", message: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const [token, setToken] = useState(() => newToken());
  const errorSummaryRef = useRef(null);

  useEffect(() => {
    if (!site.turnstileSiteKey) return;
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }, []);

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const er = {};
    if (values.fullName.trim().length < 2) er.fullName = "Please enter your full name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim())) er.email = "Please enter a valid email address.";
    if (!/^[+0-9()\-.\s]{7,22}$/.test(values.phone.trim())) er.phone = "Please enter a valid phone number.";
    if (values.message.trim().length < 10) er.message = "Please tell us a little about your project (at least 10 characters).";
    if (!values.contactMethod) er.contactMethod = "Please select a preferred contact method.";
    if (Object.keys(er).length) {
      setErrors(er);
      setFormError("Please review the highlighted fields below.");
      requestAnimationFrame(() => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }
    setSending(true);
    setFormError("");
    const turnstileToken = document.querySelector('[name="cf-turnstile-response"]')?.value || "";
    try {
      const res = await submitContact({ ...values, idempotencyToken: token, turnstileToken });
      if (res.ok) {
        trackEvent("contact_submit_success");
        toast.success("Your message has been sent.");
        setToken(newToken());
        navigate("/thank-you", { state: { type: "contact" } });
        return;
      }
      throw new Error("Unexpected response");
    } catch (err) {
      const parsed = parseError(err);
      setErrors(parsed.fieldErrors);
      setFormError(parsed.form || "We couldn't send your message. Please try again.");
      requestAnimationFrame(() => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
    } finally {
      setSending(false);
    }
  };

  const field = (key, label, type = "text", extra = {}) => (
    <div>
      <label htmlFor={`ct-${key}`} className="mb-1.5 block text-sm font-semibold text-slate-800">
        {label} <span className="text-ember" aria-hidden="true">*</span>
      </label>
      <input
        id={`ct-${key}`}
        data-testid={`contact-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`}
        type={type}
        value={values[key]}
        onChange={set(key)}
        aria-invalid={!!errors[key]}
        aria-describedby={errors[key] ? `ct-${key}-error` : undefined}
        className={inputCls}
        {...extra}
      />
      {errors[key] && (
        <p className="mt-1.5 flex items-center gap-1 text-sm font-medium text-red-600" id={`ct-${key}-error`} role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" /> {errors[key]}
        </p>
      )}
    </div>
  );

  return (
    <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="space-y-5">
      {formError && (
        <div ref={errorSummaryRef} role="alert" data-testid="contact-form-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p className="font-semibold">{formError}</p>
          <p className="mt-1">
            You can also reach us at <a href={site.phoneHref} className="font-semibold underline">{site.phone}</a> or{" "}
            <a href={site.emailHref} className="font-semibold underline break-all">{site.email}</a>.
          </p>
        </div>
      )}

      <div className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="ct-company">Company</label>
        <input id="ct-company" name="company" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {field("fullName", "Full name", "text", { autoComplete: "name", placeholder: "Your name" })}
        {field("phone", "Phone", "tel", { autoComplete: "tel", inputMode: "tel", placeholder: "(778) 555-0123" })}
      </div>
      {field("email", "Email", "email", { autoComplete: "email", inputMode: "email", placeholder: "you@example.com" })}
      <div>
        <label htmlFor="ct-contactMethod" className="mb-1.5 block text-sm font-semibold text-slate-800">
          Preferred contact method <span className="text-ember" aria-hidden="true">*</span>
        </label>
        <select
          id="ct-contactMethod"
          data-testid="contact-contact-method"
          value={values.contactMethod}
          onChange={set("contactMethod")}
          aria-invalid={!!errors.contactMethod}
          className={inputCls}
        >
          <option value="">Select…</option>
          {formOptions.contactMethods.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        {errors.contactMethod && (
          <p className="mt-1.5 flex items-center gap-1 text-sm font-medium text-red-600" role="alert">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" /> {errors.contactMethod}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="ct-message" className="mb-1.5 block text-sm font-semibold text-slate-800">
          How can we help? <span className="text-ember" aria-hidden="true">*</span>
        </label>
        <textarea
          id="ct-message"
          data-testid="contact-message"
          rows={5}
          value={values.message}
          onChange={set("message")}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "ct-message-error" : undefined}
          className={inputCls}
          placeholder="Tell us about your project or question…"
        />
        {errors.message && (
          <p className="mt-1.5 flex items-center gap-1 text-sm font-medium text-red-600" id="ct-message-error" role="alert">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" /> {errors.message}
          </p>
        )}
      </div>

      {site.turnstileSiteKey && <div className="cf-turnstile" data-sitekey={site.turnstileSiteKey} data-testid="contact-turnstile-widget" />}

      <p className="text-xs leading-relaxed text-slate-500">
        Your information is used only to respond to your enquiry — see our{" "}
        <Link to="/privacy" className="font-medium text-ember-dark underline">Privacy Policy</Link>.
      </p>

      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit-btn"
        className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-ember px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-600/25 transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto"
      >
        {sending && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
        {sending ? "Sending…" : "Send Message"}
      </button>
    </form>
  );
}
